import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/hooks/useSeo";

const DASABO_LOGO = "/logo.png";

export default function Login() {
  useSeo({
    title: "Accedi",
    description: "Accedi al tuo account Autofattura by Dasabo. Se non hai ancora un account ricevi 5 crediti omaggio alla registrazione.",
    path: "/login",
  });
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/app";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Bentornato!");
      // Admin users go straight to the admin console; regular users to /app
      // (or wherever they were trying to reach before login).
      if (u?.is_admin) {
        navigate("/app/admin", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di accesso");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF9F6]" data-testid="login-page">
      {/* Left: brand pane */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#1D4ED8] rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#F97316] rounded-full blur-3xl opacity-20" />
        <Link to="/" className="relative flex items-center gap-2 z-10">
          <span className="font-display font-extrabold text-lg flex items-center gap-1.5">
            Autofattura by
            <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto brightness-0 invert" />
          </span>
        </Link>
        <div className="relative z-10">
          <h1 className="font-display font-extrabold text-5xl xl:text-6xl leading-[0.95] tracking-tighter">
            Le fatture di{" "}
            <span className="font-serif-accent text-[#F97316]">Google Ads</span>
            <br />
            parlano italiano.
          </h1>
          <p className="mt-5 text-white/70 max-w-md text-lg leading-relaxed">
            L&apos;AI legge il PDF, tu ottieni l&apos;XML SDI. In 12 secondi.
          </p>
          <div className="mt-8 space-y-2.5 text-sm text-white/80">
            <StatRow value="13k+" label="autofatture emesse" />
            <StatRow value="12s" label="tempo medio elaborazione" />
            <StatRow value="4,7 / 5" label="rating utenti reali" />
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/40 flex items-center gap-1">
          © 2026 Dasabo srl · Palermo, Italia
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 bg-[#FAF9F6] relative">
        <div className="lg:hidden absolute top-8 left-8">
          <Link to="/" className="flex items-center gap-1.5 font-display font-extrabold text-base text-[#0A0A0A]">
            Autofattura by <img src={DASABO_LOGO} alt="Dasabo" className="h-4 w-auto" />
          </Link>
        </div>

        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter leading-[0.95] text-[#0A0A0A]">
            Bentornato,<br />
            <span className="font-serif-accent text-[#1D4ED8]">accedi</span>.
          </h2>
          <p className="text-[#4A4A4A] mt-3 text-sm">
            Continua a gestire le tue autofatture estere.
          </p>

          <label className="block mt-8 text-sm font-medium text-[#0A0A0A]">Email</label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full h-11 pl-9 pr-3 rounded-md border border-[#E8E4D9] bg-white focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors text-sm"
              placeholder="tu@studio.it"
            />
          </div>

          <label className="block mt-5 text-sm font-medium text-[#0A0A0A]">Password</label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <input
              type={showPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full h-11 pl-9 pr-10 rounded-md border border-[#E8E4D9] bg-white focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors text-sm"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              tabIndex={-1}
              data-testid="toggle-password"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting || loading}
            data-testid="login-submit-btn"
            className="group mt-8 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#0A0A0A] text-white font-semibold text-sm hover:bg-[#1D4ED8] disabled:opacity-70 shadow-lg shadow-black/10 transition-all"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                Accedi
                <span className="w-5 h-5 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight className="w-3 h-3" />
                </span>
              </>
            )}
          </button>

          <div className="mt-4 text-center text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#4A4A4A] hover:text-[#0A0A0A] hover:underline">
              Password dimenticata?
            </Link>
          </div>

          <div className="mt-6 text-center text-sm text-[#4A4A4A]">
            Non hai un account?{" "}
            <Link to="/register" className="font-semibold text-[#1D4ED8] hover:underline" data-testid="goto-register">
              Registrati
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatRow({ value, label }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-display font-extrabold text-xl text-white tracking-tight w-14">{value}</span>
      <span className="text-white/50 text-sm">{label}</span>
    </div>
  );
}
